/**
 * ExposureProgressionPanel — calm Work Mode card showing
 * Progressive Environmental Exposure for the current learner.
 *
 * Stage-2 P5 (Feb 18, 2026). Doctrine:
 *   • Advisory only — NOT route forcing, NOT auto-advance.
 *   • Collapsible. Low noise. Slate palette.
 *   • Read-only when Drive Mode = moving/unknown (glance header).
 *   • All writes (log exposure, set readiness) call the existing
 *     endpoints that enforce Drive Mode parking server-side.
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Compass, Plus, Check, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import useExposureProgression from "../../hooks/useExposureProgression";
import useDriveModeState from "../../hooks/useDriveModeState";
import { driveModeCapabilities } from "../../lib/driveMode";


function summaryHeader(summary) {
  if (!summary) return "Quiet awareness.";
  const often = summary.groups.find((g) => g.key === "often")?.count || 0;
  const rarely = summary.groups.find((g) => g.key === "rarely")?.count || 0;
  const never = summary.groups.find((g) => g.key === "never")?.count || 0;
  if (often === 0 && rarely === 0 && never > 0) {
    return "Plenty of fresh exposure ahead.";
  }
  return `Often: ${often} · Rare: ${rarely} · Not yet: ${never}`;
}


export default function ExposureProgressionPanel({
  learnerId, lessonId, surface = "dark",
}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const { movingState: localMovingState } = useDriveModeState();
  const movingState = localMovingState || "unknown";
  const driveMode = driveModeCapabilities(movingState);
  const interactive = Boolean(driveMode?.capabilities?.interactive_actions);

  const {
    summary, logExposure,
  } = useExposureProgression({
    learnerId,
    enabled: Boolean(learnerId),
  });

  const headerLine = useMemo(() => summaryHeader(summary), [summary]);

  if (!learnerId) return null;

  const isDark = surface === "dark";
  const wrapCls = isDark
    ? "bg-slate-800/70 border-slate-700 text-slate-200"
    : "bg-slate-50 border-slate-200 text-slate-700";
  const dividerCls = isDark ? "border-slate-700/60" : "border-slate-200/60";
  const labelMutedCls = isDark ? "text-slate-400/80" : "text-slate-500";
  const subTextCls = isDark ? "text-slate-300" : "text-slate-600";

  const expandable = interactive;
  const isOpen = expandable && open;

  const onLog = async (cat) => {
    setFeedback(null);
    const res = await logExposure({
      category: cat.category,
      movingState,
      lessonId,
    });
    if (res.ok) {
      setFeedback({ tone: "ok", text: `Quietly noted exposure for ${cat.label}.` });
    } else if (res.code === "drive_mode_glance_only") {
      setFeedback({ tone: "muted", text: "Glance-only while moving. Log when parked." });
    } else {
      setFeedback({ tone: "muted", text: res.message || "Could not log." });
    }
    setTimeout(() => setFeedback(null), 3500);
  };

  return (
    <div
      data-testid="exposure-progression-panel"
      data-moving-state={movingState}
      data-interactive={interactive ? "true" : "false"}
      className={`rounded-xl border ${wrapCls} overflow-hidden`}
    >
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="exposure-progression-toggle"
          aria-expanded={isOpen}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
        >
          <Compass className="w-4 h-4 flex-none opacity-70" />
          <div className="flex-1 min-w-0">
            <div
              data-testid="exposure-progression-heading"
              className="text-[11px] font-semibold uppercase tracking-wide opacity-70"
            >
              Exposure breadth
            </div>
            <div
              data-testid="exposure-progression-summary"
              className="text-sm font-semibold truncate"
            >
              {headerLine}
            </div>
          </div>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 flex-none opacity-50" />
          ) : (
            <ChevronDown className="w-4 h-4 flex-none opacity-50" />
          )}
        </button>
      ) : (
        <div
          data-testid="exposure-progression-glance"
          className="w-full flex items-center gap-3 px-4 py-3"
        >
          <Compass className="w-4 h-4 flex-none opacity-70" />
          <div className="flex-1 min-w-0">
            <div
              data-testid="exposure-progression-heading"
              className="text-[11px] font-semibold uppercase tracking-wide opacity-70"
            >
              Exposure breadth
            </div>
            <div
              data-testid="exposure-progression-summary"
              className="text-sm font-semibold truncate"
            >
              {headerLine}
            </div>
          </div>
        </div>
      )}

      {isOpen ? (
        <div
          data-testid="exposure-progression-body"
          className={`px-4 pb-4 pt-1 text-sm border-t ${dividerCls} ${subTextCls}`}
        >
          {summary.groups.map((g) => {
            if (g.items.length === 0) return null;
            return (
              <section
                key={g.key}
                data-testid={`exposure-group-${g.key}`}
                className="mb-3"
              >
                <div className={`text-[11px] uppercase tracking-wide ${labelMutedCls} mb-1.5`}>
                  {g.label} · {g.count}
                </div>
                <ul className="space-y-1.5">
                  {g.items.map((cat) => (
                    <li
                      key={cat.category}
                      data-testid={`exposure-cat-${cat.category}`}
                      data-bucket={g.key}
                      className="flex items-start gap-2"
                    >
                      <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-none ${
                        isDark ? "bg-slate-400" : "bg-slate-500"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium leading-snug">
                          {cat.label}
                        </div>
                        <div className={`text-[11px] ${labelMutedCls} leading-snug`}>
                          {cat.suggestion}
                          {cat.instructor_confirmed ? " · ready confirmed" : null}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          <button
                            type="button"
                            onClick={() => onLog(cat)}
                            data-testid={`exposure-log-${cat.category}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium underline-offset-2 hover:underline ${
                              isDark ? "text-slate-200" : "text-slate-700"
                            }`}
                          >
                            <Plus className="w-3 h-3 opacity-70" />
                            Log exposure
                          </button>
                          {/* B0 Step 4 — inline understanding-hub link.
                              Advisory only, never auto-routed; surfaces
                              only when the category maps 1:1 to a hub. */}
                          {cat.understanding_hub_path ? (
                            <Link
                              to={cat.understanding_hub_path}
                              data-testid={`exposure-hub-link-${cat.category}`}
                              data-hub={cat.understanding_hub_path}
                              className={`inline-flex items-center gap-1 text-[11px] font-medium underline-offset-2 hover:underline ${
                                isDark ? "text-slate-200" : "text-slate-700"
                              }`}
                            >
                              <ExternalLink className="w-3 h-3 opacity-70" />
                              Open the {cat.understanding_hub_label} hub
                            </Link>
                          ) : null}
                          {cat.exposure_count > 0 ? (
                            <span className={`text-[11px] ${labelMutedCls}`}>
                              × {cat.exposure_count}
                              {cat.max_complexity_seen
                                ? ` · L${cat.max_complexity_seen}`
                                : ""}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          {feedback ? (
            <div
              data-testid="exposure-progression-feedback"
              data-tone={feedback.tone}
              className={`text-[11px] mt-2 inline-flex items-center gap-1.5 ${
                feedback.tone === "ok"
                  ? (isDark ? "text-slate-100" : "text-slate-800")
                  : labelMutedCls
              }`}
            >
              {feedback.tone === "ok" ? <Check className="w-3 h-3 opacity-70" /> : null}
              <span>{feedback.text}</span>
            </div>
          ) : null}

          <p className={`mt-3 text-[11px] ${labelMutedCls}`}>
            Advisory only — no auto-routes, no auto-advance. You stay in control.
          </p>
        </div>
      ) : null}
    </div>
  );
}
